import React from 'react';
import { Mail, Phone, MessageCircle, FileText, Book, Youtube, HelpCircle, ExternalLink, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PublicHeader } from '../PublicHeader';
import { Footer } from '../Footer';

export function SupportPage() {
  return (
    <div className="min-h-screen flex flex-col bg-bw-app">
      <PublicHeader />
      
      <div className="flex-grow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="relative bg-bw-sidebar rounded-lg shadow-lg p-12 mb-12 overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff_1px,transparent_1px),linear-gradient(to_bottom,#ffffff_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>
            </div>
            
            <div className="relative text-center text-white">
              <div className="flex items-center justify-center mb-6">
                <HelpCircle className="h-12 w-12 text-bw-accent" />
              </div>
              <h1 className="text-4xl font-bold mb-4">Help & Support</h1>
              <p className="text-white/90 max-w-2xl mx-auto">
                Get the help you need to succeed with BidWin's AI-powered contract bidding platform.
              </p>
            </div>
          </div>

          {/* Quick Actions Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="bg-bw-surface rounded-xl shadow-lg p-8 transform hover:scale-[1.02] transition-all duration-300">
              <MessageCircle className="h-8 w-8 text-bw-primary mb-4" />
              <h3 className="text-lg font-semibold mb-2">Live Chat Support</h3>
              <p className="text-bw-muted mb-4">Get real-time assistance from our support team</p>
              <button className="text-bw-primary hover:text-bw-accent font-medium flex items-center">
                Start Chat
                <ExternalLink className="h-4 w-4 ml-2" />
              </button>
            </div>

            <div className="bg-bw-surface rounded-xl shadow-lg p-8 transform hover:scale-[1.02] transition-all duration-300">
              <Mail className="h-8 w-8 text-bw-primary mb-4" />
              <h3 className="text-lg font-semibold mb-2">Email Support</h3>
              <p className="text-bw-muted mb-4">Get help via email within 24 hours</p>
              <a 
                href="mailto:support@bidwin.com" 
                className="text-bw-primary hover:text-bw-accent font-medium flex items-center"
              >
                support@bidwin.com
                <ExternalLink className="h-4 w-4 ml-2" />
              </a>
            </div>

            <div className="bg-bw-surface rounded-xl shadow-lg p-8 transform hover:scale-[1.02] transition-all duration-300">
              <Phone className="h-8 w-8 text-bw-primary mb-4" />
              <h3 className="text-lg font-semibold mb-2">Phone Support</h3>
              <p className="text-bw-muted mb-4">Talk to us directly during business hours</p>
              <a 
                href="tel:1-800-BID-WIN1" 
                className="text-bw-primary hover:text-bw-accent font-medium flex items-center"
              >
                1-800-BID-WIN1
                <ExternalLink className="h-4 w-4 ml-2" />
              </a>
            </div>
          </div>

          {/* Resources Section */}
          <div className="bg-bw-surface rounded-xl shadow-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-bw-ink mb-6">Resources</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Link 
                to="/docs" 
                className="flex items-start p-4 rounded-lg hover:bg-bw-surface-subtle transition-colors group"
              >
                <Book className="h-6 w-6 text-bw-primary mt-1 flex-shrink-0" />
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-bw-ink group-hover:text-bw-primary">Documentation</h3>
                  <p className="mt-1 text-bw-muted">
                    Detailed guides and API documentation
                  </p>
                </div>
                <ExternalLink className="h-5 w-5 text-bw-muted ml-auto flex-shrink-0 group-hover:text-bw-primary" />
              </Link>

              <Link 
                to="/tutorials" 
                className="flex items-start p-4 rounded-lg hover:bg-bw-surface-subtle transition-colors group"
              >
                <Youtube className="h-6 w-6 text-bw-primary mt-1 flex-shrink-0" />
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-bw-ink group-hover:text-bw-primary">Video Tutorials</h3>
                  <p className="mt-1 text-bw-muted">
                    Step-by-step video guides
                  </p>
                </div>
                <ExternalLink className="h-5 w-5 text-bw-muted ml-auto flex-shrink-0 group-hover:text-bw-primary" />
              </Link>

              <Link 
                to="/blog" 
                className="flex items-start p-4 rounded-lg hover:bg-bw-surface-subtle transition-colors group"
              >
                <FileText className="h-6 w-6 text-bw-primary mt-1 flex-shrink-0" />
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-bw-ink group-hover:text-bw-primary">The Winning Bid</h3>
                  <p className="mt-1 text-bw-muted">
                    Tips and strategies for successful bidding
                  </p>
                </div>
                <ExternalLink className="h-5 w-5 text-bw-muted ml-auto flex-shrink-0 group-hover:text-bw-primary" />
              </Link>

              <Link 
                to="/faq" 
                className="flex items-start p-4 rounded-lg hover:bg-bw-surface-subtle transition-colors group"
              >
                <HelpCircle className="h-6 w-6 text-bw-primary mt-1 flex-shrink-0" />
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-bw-ink group-hover:text-bw-primary">FAQs</h3>
                  <p className="mt-1 text-bw-muted">
                    Common questions and answers
                  </p>
                </div>
                <ExternalLink className="h-5 w-5 text-bw-muted ml-auto flex-shrink-0 group-hover:text-bw-primary" />
              </Link>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-bw-surface rounded-xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-bw-ink mb-6">Send us a Message</h2>
            <form className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-bw-ink">
                    Name
                  </label>
                  <input
                    type="text"
                    className="mt-1 block w-full rounded-md border-bw-border shadow-sm focus:border-bw-primary focus:ring-bw-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-bw-ink">
                    Email
                  </label>
                  <input
                    type="email"
                    className="mt-1 block w-full rounded-md border-bw-border shadow-sm focus:border-bw-primary focus:ring-bw-primary"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-bw-ink">
                  Subject
                </label>
                <input
                  type="text"
                  className="mt-1 block w-full rounded-md border-bw-border shadow-sm focus:border-bw-primary focus:ring-bw-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-bw-ink">
                  Message
                </label>
                <textarea
                  rows={4}
                  className="mt-1 block w-full rounded-md border-bw-border shadow-sm focus:border-bw-primary focus:ring-bw-primary"
                ></textarea>
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-bw-primary hover:bg-bw-primary/90"
                >
                  <Zap className="h-5 w-5 mr-2" />
                  Send Message
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}